import React, { useContext, useEffect, useState } from 'react';
import { ReleaseImporter } from './Importer/ReleaseImporter';
import {
    MessageBar,
    MessageBarType,
    Stack,
    Text,
    TooltipHost,
} from '@fluentui/react';
import { AdminComponent } from './AdminComponent';
import { BannerList } from './Banner/BannerList';
import {
    GlobalConfigProperties,
    getConfig,
    getAllConfig,
} from '../../Common/api/ABHub/ABHubGlobalConfig';
import { AdminEndpoints } from './AdminEndpoints/AdminEndpoints';
import { ThemeContext } from '../../HubLayout/models/ThemeContext';
import { getThemeFromString } from '../../Common/util/localStorageUtils';
import { useIsMobile } from '../../Common/hooks/useIsMobile';
import { HubHeader } from '../../Common/components/HubHeader';

// ConfigItem component for displaying configuration values
interface ConfigItemProps {
    label: string;
    value: string;
    configKey: string;
    theme: any;
    isUrl?: boolean;
}

const ConfigItem: React.FC<ConfigItemProps> = ({
    label,
    value,
    configKey,
    theme,
    isUrl,
}) => {
    const displayValue = value || 'Not configured';
    const truncatedValue =
        isUrl && value
            ? value.length > 40
                ? `${value.substring(0, 37)}...`
                : value
            : displayValue;

    return (
        <div
            style={{
                padding: '8px 12px',
                backgroundColor: theme.palette.neutralLighter,
                borderRadius: '4px',
                borderLeft: `3px solid ${theme.palette.themePrimary}`,
            }}
        >
            <Text
                variant="small"
                styles={{
                    root: {
                        color: theme.palette.neutralSecondary,
                        display: 'block',
                        marginBottom: '4px',
                        fontWeight: 600,
                    },
                }}
            >
                {label}
            </Text>
            {isUrl && value ? (
                <TooltipHost content={value}>
                    <Text
                        variant="small"
                        styles={{
                            root: {
                                color: theme.palette.neutralPrimary,
                                fontSize: '11px',
                                wordBreak: 'break-all',
                            },
                        }}
                    >
                        {truncatedValue}
                    </Text>
                </TooltipHost>
            ) : (
                <Text
                    variant="small"
                    styles={{
                        root: {
                            color: value
                                ? theme.palette.neutralPrimary
                                : theme.palette.neutralTertiary,
                            fontWeight: value ? 600 : 400,
                        },
                    }}
                >
                    {displayValue}
                </Text>
            )}
            <Text
                variant="tiny"
                styles={{
                    root: {
                        color: theme.palette.neutralTertiary,
                        display: 'block',
                        marginTop: '2px',
                        fontSize: '10px',
                        fontStyle: 'italic',
                    },
                }}
            >
                {configKey}
            </Text>
        </div>
    );
};

// Helper to format config key names to friendly labels
const formatConfigLabel = (key: string): string => {
    return key
        .split('_')
        .map(
            (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        )
        .join(' ');
};

export const Administration: React.FC = () => {
    const themeContext = useContext(ThemeContext);
    const theme = getThemeFromString(themeContext.themeName);
    const isMobile = useIsMobile();

    const environment = getConfig(GlobalConfigProperties.Environment);
    const [validEnv, setValidEnv] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [allConfigValues, setAllConfigValues] = useState<
        Record<string, string>
    >({});

    useEffect(() => {
        setValidEnv(
            environment === 'Development' ||
                environment === 'public.dev' ||
                environment === 'Test' ||
                environment === 'public.sovmock'
        );

        // Load all config values
        setAllConfigValues(getAllConfig());
    }, [environment]);

    return (
        <div
            style={{
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <div
                style={{
                    paddingTop: isMobile ? '16px' : '0',
                    maxWidth: '100%',
                }}
            >
                {/* Top Header Container */}
                <div
                    style={{
                        backgroundColor: theme.palette.white,
                        borderRadius: '4px',
                        boxShadow:
                            '0 3.2px 7.2px 0 rgba(0, 0, 0, .132), 0 .6px 1.8px 0 rgba(0, 0, 0, .108)',
                        overflow: 'hidden',
                        marginBottom: '16px',
                    }}
                >
                    {/* Header */}
                    <HubHeader
                        title="Administration"
                        subtitle="System administration and configuration tools"
                    />

                    {/* Content */}
                    <div style={{ padding: isMobile ? '16px' : '20px' }}>
                        {errorMessage && (
                            <MessageBar
                                messageBarType={MessageBarType.error}
                                isMultiline={true}
                                onDismiss={() => setErrorMessage(null)}
                                dismissButtonAriaLabel="Close"
                                styles={{
                                    root: {
                                        marginBottom: '16px',
                                        backgroundColor: theme.palette.redDark,
                                    },
                                    text: {
                                        color: theme.palette.white,
                                    },
                                    icon: {
                                        color: theme.palette.white,
                                    },
                                    dismissal: {
                                        color: theme.palette.white,
                                    },
                                }}
                            >
                                {errorMessage}
                            </MessageBar>
                        )}
                        {successMessage && (
                            <MessageBar
                                messageBarType={MessageBarType.success}
                                isMultiline={false}
                                onDismiss={() => setSuccessMessage(null)}
                                dismissButtonAriaLabel="Close"
                                styles={{
                                    root: {
                                        marginBottom: '16px',
                                        backgroundColor: theme.palette.green,
                                    },
                                    text: {
                                        color: theme.palette.white,
                                    },
                                    icon: {
                                        color: theme.palette.white,
                                    },
                                    dismissal: {
                                        color: theme.palette.white,
                                    },
                                }}
                            >
                                {successMessage}
                            </MessageBar>
                        )}

                        {/* Global Configuration Display */}
                        <div
                            style={{
                                display: 'grid',
                                gridTemplateColumns: isMobile
                                    ? '1fr'
                                    : 'repeat(auto-fit, minmax(200px, 1fr))',
                                gap: '12px',
                                marginBottom: '16px',
                            }}
                        >
                            {Object.entries(allConfigValues).map(
                                ([key, value]) => {
                                    const isUrl =
                                        key.includes('url') ||
                                        key.includes('Url');
                                    return (
                                        <ConfigItem
                                            key={key}
                                            label={formatConfigLabel(key)}
                                            value={value}
                                            configKey={key}
                                            theme={theme}
                                            isUrl={isUrl}
                                        />
                                    );
                                }
                            )}
                        </div>
                    </div>
                </div>

                {/* Admin Sections */}
                <Stack tokens={{ childrenGap: 16 }}>
                    <AdminComponent
                        title="Banner Tool"
                        component={<BannerList />}
                        defaultExpanded={true}
                    />

                    <AdminComponent
                        title="Admin Endpoints"
                        component={<AdminEndpoints />}
                        defaultExpanded={true}
                    />

                    {validEnv && (
                        <AdminComponent
                            title="Release Importer"
                            component={<ReleaseImporter />}
                        />
                    )}
                </Stack>
            </div>
        </div>
    );
};
